import type { Metadata } from '@atlaskit/editor-common/collab';

import {
	queryInterop,
	type QueryInteropSupportedResult,
} from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { isCollabDraftCacheStale } from './CollabDraftCacheManager.ts';
import CollabDraftQuery, {
	type CollabDraftQuery$data,
	type CollabDraftQuery$variables,
} from './__generated__/CollabDraftQuery.graphql.ts';

export type InitialDraft = {
	document: any;
	version: number;
	metadata?: Metadata;
};

export type FetchDraft = {
	draft: InitialDraft | null;
	response?: Response | QueryInteropSupportedResult<CollabDraftQuery$data>;
	error?: Error;
};

export async function fetchDraft(contentId: string, draftShareId?: string): Promise<FetchDraft> {
	// Use cache-first only if SSR rendered AND cache is not stale (within TTL)
	// This prevents stale data during SPA transitions while still benefiting from SSR cache
	const shouldUseCache = expValEquals(
		'platform_editor_collabdraft_query_improvements',
		'isEnabled',
		true,
	)
		? window.__SSR_RENDERED__ || !isCollabDraftCacheStale(contentId, draftShareId)
		: window.__SSR_RENDERED__;

	const response = await queryInterop<CollabDraftQuery$data, CollabDraftQuery$variables>({
		query: CollabDraftQuery,
		variables: {
			id: contentId,
			hydrateAdf: true,
			...(draftShareId ? { draftShareId } : undefined),
		},
		fetchPolicy: shouldUseCache ? 'cache-first' : 'network-only',
	});

	const { data, errors } = response;

	if ((errors && errors.length > 0) || !data?.collabDraft) {
		return { draft: null, response };
	}

	const { document, version, metadata } = data.collabDraft;

	const draft = {
		version,
		metadata: excludeTypename(metadata),
		document: JSON.parse(document || ''),
	} as InitialDraft;

	return { draft, response };
}

function excludeTypename(
	metadata?: Record<string, any> | null,
): Record<string, any> | null | undefined {
	if (!metadata) return metadata;
	const { __typename, ...rest } = metadata;
	return rest;
}
